import { AgGridCellTooltipComponent, AgGridCellTooltipValueGetter } from 'components/AgGrid/AgGridCellTooltipComponent';
import { AgGridChoiceFilterAdapter, AgGridCustomFilterAdapter } from 'components/CustomTableFilter';
import { UerChapter, UerClassifier, UerStatus, WorkInSumTitles } from 'constantsProject';
import { useResetMultipleStoreData } from 'features/request/useResetStoreData';
import type {
    ConstructionTypeData,
    ContractStatusEnum,
    FilterValueOfNullableOfChapterEnum,
    Maybe,
    ProjectData,
    WorkInSumEnum,
} from 'gql/graphql';
import { SortEnumType } from 'gql/graphql';
import type { IUseAccess } from 'hooks/useAccess';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { formatNumberToRu } from 'utils';
import { getYesNoStatus } from 'utils/getYesNoTableStatus';
import { sortItemsFilterWithNullable } from 'utils/sortItemsFilterWithNulleble';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import * as R from 'ramda';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
    UerMenuCellRendererComponent,
    UerUnitCostWithOtherCellRendererComponent,
    UnitCostWithOtherHeader,
} from './cellRenderers';
import type { RowData } from './types';
import {
    priceListUerChapterFilter,
    priceListUerCodeFilter,
    priceListUerGroupFullNameFilter,
    priceListUerJustificationFilter,
    priceListUerNameFilter,
    priceListUerSectionNameFilter,
    priceListUerStatusFilter,
    priceListUerTypeFilter,
    priceListUerWorkInSumFilter,
    priceListUerWorkNameFilter,
} from './uerQueries';

const ListItemsCostWidth: Record<string, number> = {
    unitCostWithOther: 130,
    ozp: 110,
    emm: 110,
    zpm: 70,
    mtr: 110,
    eqp: 90,
    pir: 70,
    trn: 110,
    nr: 110,
    sp: 110,
};

export const listItemsCostPriceList = {
    unitCostWithOther: 'Итого',
    ozp: 'ОЗП',
    emm: 'ЭММ',
    zpm: 'ЗПМ',
    mtr: 'МТР',
    eqp: 'ОБ',
    pir: 'ПИР',
    trn: 'Перевозка',
    nr: 'НР',
    sp: 'СП',
};

const CollapseKeys = R.tail(Object.keys(listItemsCostPriceList));

const sortChapterItems = (items: Maybe<FilterValueOfNullableOfChapterEnum[] | undefined>) =>
    sortItemsFilterWithNullable(items, UerChapter)?.sort((a, b) => a.value?.localeCompare(b?.value));

interface IUsePriceListUerColumns {
    constructionTypes: ConstructionTypeData[];
    contractId: ProjectData['contractId'];
    contractStatus: ContractStatusEnum | undefined;
    handleExpandRow: (id: RowData['id']) => void;
    unreadyUerRowCount: number | undefined;
    hasWorkKind: boolean | null | undefined;
    priceListRules: IUseAccess['priceList'];
}

export const usePriceListUerColumns = ({
    constructionTypes,
    contractId,
    contractStatus,
    handleExpandRow,
    unreadyUerRowCount,
    hasWorkKind,
    priceListRules,
}: IUsePriceListUerColumns) => {
    const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});

    const memoizedTypeAndChapterIdFilters = useMemo(
        () => ['uerPriceListUerTypeFilter', 'uerPriceListUerChapterFilter'],
        [],
    );

    const [resetDataDependentFilters] = useResetMultipleStoreData({
        requestIds: memoizedTypeAndChapterIdFilters,
    });

    const variables = useMemo(() => ({ contractId }), [contractId]);
    const variablesWorkName = useMemo(
        () => ({
            contractId,
            order: [{ value: SortEnumType.Asc }],
        }),
        [contractId],
    );

    const changeFilterHandler = useCallback<(params: { fieldName: string; checkedValues: string[] }) => void>(
        ({ fieldName, checkedValues }) => {
            setFilterValues((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues,
            }));
        },
        [],
    );

    const { savedFilterState, handleSaveFilterState } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    'uerList',
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterValues,
                    }),
                ),
            }),
            [changeFilterHandler],
        ),
    );

    useEffect(() => {
        const { uerList: uerListFilters } = savedFilterState ?? {};

        const chapterFilterHasEmptyValue = uerListFilters?.chapter?.some(
            (item: { value: string | null }) => !item?.value,
        );

        const typeFilterHasEmptyValue = uerListFilters?.uerClassifier?.some(
            (item: { value: string | null }) => !item?.value,
        );

        if ((typeFilterHasEmptyValue || chapterFilterHasEmptyValue) && !unreadyUerRowCount) {
            handleSaveFilterState({
                key: 'uerList',
                newState: {
                    ...uerListFilters,
                    chapter: uerListFilters?.chapter?.filter((item: { value: string | null }) => item?.value),
                    uerClassifier: uerListFilters?.uerClassifier?.filter(
                        (item: { value: string | null }) => item?.value,
                    ),
                },
            });
        }
    }, [changeFilterHandler, handleSaveFilterState, savedFilterState, unreadyUerRowCount]);

    const columns = useMemo<(ColDef<RowData> | ColGroupDef<RowData>)[]>(
        () => [
            {
                field: 'menu' as any,
                headerName: '',
                width: 65,
                spanHeaderHeight: true,
                pinned: 'left',
                lockPosition: 'left',
                resizable: false,
                cellRenderer: UerMenuCellRendererComponent,
                cellRendererParams: {
                    contractStatus,
                    handleExpandRow,
                    priceListRules,
                },
                cellClass: 'center-aligned-cell',
            },
            {
                field: 'sectionName',
                headerName: 'Раздел',
                width: 210,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerSectionNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Раздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: priceListUerSectionNameFilter,
                },
            },
            {
                field: 'groupFullName',
                headerName: 'Подраздел',
                width: 400,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerGroupFullNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Подраздел',
                            field: 'value',
                            checkboxSelection: true,
                            width: 400,
                        },
                    ],
                    query: priceListUerGroupFullNameFilter,
                },
            },
            {
                field: 'code',
                headerName: 'Шифр',
                width: 150,
                spanHeaderHeight: true,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerCodeFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Шифр',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: priceListUerCodeFilter,
                },
            },
            {
                field: 'name',
                headerName: 'Наименование',
                minWidth: 400,
                flex: 1,
                spanHeaderHeight: true,
                tooltipComponent: AgGridCellTooltipComponent,
                tooltipValueGetter: AgGridCellTooltipValueGetter({}),
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerNameFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Наименование',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Новый>',
                            width: 400,
                        },
                    ],
                    query: priceListUerNameFilter,
                },
            },
            {
                field: 'unit',
                headerName: 'Ед.изм.',
                width: 90,
                spanHeaderHeight: true,
            },
            {
                field: 'chapter',
                headerName: 'Глава в расчете',
                width: 115,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => UerChapter[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerChapterFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Глава в расчете',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => UerChapter[value] || '<Нет значения>',
                            width: 400,
                        },
                    ],
                    query: priceListUerChapterFilter,
                    groupAndSortItemsHelper: sortChapterItems,
                },
            },
            {
                field: 'uerClassifier',
                headerName: 'Тип УЕР',
                width: 200,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => UerClassifier[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerTypeFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Тип УЕР',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => UerClassifier[value] || '<Нет значения>',
                            width: 400,
                        },
                    ],
                    query: priceListUerTypeFilter,
                    groupAndSortItemsHelper: sortItemsFilterWithNullable,
                },
            },
            {
                headerName:
                    'Стоимость единицы УЕР в текущем уровне цен (с учетом прочих и лимитированных затрат), руб.без НДС',
                marryChildren: true,
                children: constructionTypes.map(({ shortName, id }) => ({
                    headerName: shortName,
                    marryChildren: true,
                    children: Object.entries(listItemsCostPriceList).map(([key, headerName], index) => ({
                        field: `${id}${key}`,
                        headerName,
                        cellClass: 'textRight',
                        width: ListItemsCostWidth[key],
                        spanHeaderHeight: true,
                        valueFormatter: ({ data }) => {
                            const value = data?.currentPrices.find(
                                ({ constructionTypeId }) => constructionTypeId === id,
                            )?.[key as keyof RowData['currentPrices'][number]];

                            return value ? formatNumberToRu(value, 2) : value ?? '';
                        },
                        hide: !!index,
                        headerComponent: index ? undefined : UnitCostWithOtherHeader,
                        headerComponentParams: index ? undefined : { collapseKeys: CollapseKeys, id },
                        cellRenderer: index ? undefined : UerUnitCostWithOtherCellRendererComponent,
                        cellRendererParams: index
                            ? undefined
                            : {
                                  constructionTypeId: id,
                                  isExpanded: false,
                              },
                    })),
                })),
            },
            {
                field: 'status',
                headerName: 'Статус записи',
                width: 150,
                spanHeaderHeight: true,
                valueFormatter: ({ value }) => UerStatus[value] ?? '',
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerStatusFilter',
                    withSearch: false,
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Статус записи',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => UerStatus[value],
                            width: 400,
                        },
                    ],
                    query: priceListUerStatusFilter,
                },
            },
            {
                headerName: 'Используется',
                marryChildren: true,
                children: [
                    {
                        field: 'isIncludedInWorkOrder',
                        headerName: 'НЗ',
                        width: 105,
                        spanHeaderHeight: true,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            hideTableHeader: true,
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInAct',
                        headerName: 'Акт',
                        width: 105,
                        spanHeaderHeight: true,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                    {
                        field: 'isIncludedInWorkOrderChange',
                        headerName: 'Изм. к НЗ',
                        headerClass: 'formatTitleWidth',
                        width: 105,
                        spanHeaderHeight: true,
                        valueFormatter: ({ value }) => getYesNoStatus(value),
                        filter: AgGridChoiceFilterAdapter,
                        filterParams: {
                            changeFilterHandler,
                            filterItems: [
                                {
                                    name: 'Да',
                                    value: 'Да',
                                },
                                { name: 'Нет', value: 'Нет' },
                            ],
                        },
                    },
                ],
            },
            ...(hasWorkKind
                ? [
                      {
                          headerName: 'Виды работ',
                          marryChildren: true,
                          children: [
                              {
                                  field: 'workCode',
                                  headerName: 'Шифр',
                                  width: 140,
                                  spanHeaderHeight: true,
                                  valueFormatter: ({ data }: { data: RowData | undefined }) => data?.workKind?.workCode,
                              },
                              {
                                  field: 'workName',
                                  headerName: 'Наименование',
                                  width: 170,
                                  spanHeaderHeight: true,
                                  valueFormatter: ({ data }: { data: RowData | undefined }) => data?.workKind?.workName,
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      variables: variablesWorkName,
                                      changeFilterHandler,
                                      dataAccessor: 'uerPriceListUerWorkNameFilter',
                                      whereVariables: ['value'],
                                      childFieldAccessor: 'value',
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              width: 400,
                                              valueFormatter: ({ value }: { value: string | undefined }) =>
                                                  value ?? '<Нет значения>',
                                          },
                                      ],
                                      query: priceListUerWorkNameFilter,
                                  },
                              },
                              {
                                  field: 'workUnit',
                                  headerName: 'Ед.изм.',
                                  width: 90,
                                  spanHeaderHeight: true,
                                  valueFormatter: ({ data }: { data: RowData | undefined }) => data?.workKind?.workUnit,
                              },
                              {
                                  field: 'workInSum',
                                  headerName: 'Участие в суммировании ФО по виду работ',
                                  width: 170,
                                  spanHeaderHeight: true,
                                  valueFormatter: ({ data }: { data: RowData | undefined }) =>
                                      WorkInSumTitles[data?.workKind?.workInSum as WorkInSumEnum],
                                  filter: AgGridCustomFilterAdapter,
                                  filterParams: {
                                      hideTableHeader: true,
                                      withSearch: false,
                                      variables,
                                      changeFilterHandler,
                                      dataAccessor: 'uerPriceListUerWorkInSumFilter',
                                      whereVariables: ['value'],
                                      childFieldAccessor: 'value',
                                      columns: [
                                          {
                                              headerName: 'Наименование',
                                              field: 'value',
                                              checkboxSelection: true,
                                              width: 400,
                                              cellRenderer: ({ value }: { value: string }) =>
                                                  WorkInSumTitles[value as WorkInSumEnum],
                                          },
                                      ],
                                      query: priceListUerWorkInSumFilter,
                                  },
                              },
                              {
                                  field: 'workRatio',
                                  headerName: 'К. перехода',
                                  width: 120,
                                  spanHeaderHeight: true,
                                  valueFormatter: ({ data }: { data: RowData | undefined }) =>
                                      formatNumberToRu(data?.workKind?.workRatio, 4),
                              },
                          ],
                      },
                  ]
                : []),
            {
                field: 'justification',
                headerName: 'Обоснование',
                minWidth: 155,
                flex: 1,
                filter: AgGridCustomFilterAdapter,
                filterParams: {
                    hideTableHeader: true,
                    variables,
                    changeFilterHandler,
                    dataAccessor: 'uerPriceListUerJustificationFilter',
                    whereVariables: ['value'],
                    childFieldAccessor: 'value',
                    columns: [
                        {
                            headerName: 'Обоснование',
                            field: 'value',
                            checkboxSelection: true,
                            cellRenderer: ({ value }: { value: string }) => value || '<Нет значения>',
                            width: 400,
                        },
                    ],
                    query: priceListUerJustificationFilter,
                    groupAndSortItemsHelper: sortItemsFilterWithNullable,
                },
            },
        ],
        [
            changeFilterHandler,
            constructionTypes,
            contractStatus,
            handleExpandRow,
            hasWorkKind,
            priceListRules,
            variables,
            variablesWorkName,
        ],
    );

    return useMemo(
        () => ({
            columns,
            filterValues,
            resetDataDependentFilters,
        }),
        [columns, filterValues, resetDataDependentFilters],
    );
};
